/**
 * Vitrine PWA card-side API — карта, balance, transactions, sensitive reveal.
 * Mirrors backend ShowcaseMeController (/api/v1/showcase/me/*).
 */
import { http } from './http';
import { downloadBlob } from '@/utils/blob';

/**
 * How long an upload may take before we give up on it.
 *
 * <p>Five minutes is not generosity, it is arithmetic: the screen allows 20 MB per file, and a phone on
 * a weak mobile connection uploads at a few hundred kilobits per second. The shared 30 s budget covers
 * roughly a megabyte on such a link, which is why a perfectly ordinary passport photo could not get
 * through at all. Kept finite on purpose — an upload that really is dead should end with a message the
 * client can act on, not with a progress bar that never moves.</p>
 */
const UPLOAD_TIMEOUT_MS = 300_000;

/**
 * Per Round 19 M5 — me/logout, me/password/change живут в этом модуле (не vitrineAuthApi),
 * т.к. endpoints на ShowcaseMeController (под /me/*, не /auth/*). Named exports чтобы
 * импорты были explicit.
 */
export interface MyProfile {
  customerId: string;
  firstName: string;
  lastName: string;
  email: string;
  preferredLanguage: string;
}

export interface MyCard {
  cardId: string;
  status: 'REQUESTED' | 'ACTIVE' | 'FROZEN' | 'BLOCKED' | 'CLOSED' | 'EXPIRED';
  currency: string;
  lastFour: string;
  holderName: string;
  network: string;
  tier: string;
  expiryMonth: number;
  expiryYear: number;
  balanceAvailable: boolean;
  transactionsAvailable: boolean;
  /** The client's own name for the card (owner decision В-5); null/absent = the default title. */
  name?: string | null;
}

/** Longest card name the server accepts, in characters (ShowcaseCardManageService.NAME_MAX_LENGTH). */
export const CARD_NAME_MAX_LENGTH = 40;

/**
 * Issuance offer shown on the no-card hero (docs/CLIENT_KYC_AND_CARD_ISSUANCE.md §4). Sourced from the
 * tariff assigned to the client at provisioning — price/benefits/offer-PDF are backend data, never hardcoded
 * on the frontend. Any field may be null when the tariff has no offer configured yet.
 */
export interface IssuanceOffer {
  tariffId: string;
  cardPriceMinor: number | null;
  currency: string | null;
  /** Localized benefits/conditions markdown map {en,ru,zh} (same shape as the FAQ answer). Null when unset. */
  benefits: Record<string, string> | null;
  /** Provider-level localized legal offer Markdown. */
  offerText: Record<string, string> | null;
  /** Locale → managed PDF ref; values are availability markers only. */
  offerPdfRefs: Record<string, string> | null;
  offerPdfRef: string | null;
  offerVersion: string | null;
  /**
   * The access-plan wording of the tariff (BO «Plan card», 09 Д-10.4): name above the price, period after it, a line
   * under it — each a locale map {en,ru,zh}. Null/absent when the tariff has none (or on an older server).
   */
  plan?: OfferPlanTexts | null;
}

/** Locale maps of the plan card; any of them may be null — that line is then left out. */
export interface OfferPlanTexts {
  name: Record<string, string> | null;
  period: Record<string, string> | null;
  note: Record<string, string> | null;
}

/**
 * Card-side landing state (docs §3). A card-less client is a NORMAL state, not a 404 — when hasCard=false
 * the client sees the issuance offer instead of the card UI.
 *  - hasCard=true  → card populated, offer null.
 *  - hasCard=false → card null, offer populated (fields may be null).
 */
export interface CardOverview {
  hasCard: boolean;
  issuanceState: string;
  card: MyCard | null;
  offer: IssuanceOffer | null;
}

/**
 * Server-owned onboarding routing (docs/ONBOARDING_BUG_REVIEW_2026-08-15.md §7.1). `nextAction` is the ONLY
 * thing a screen should branch on — never `hasCard` plus a hand-rolled status list, which is how a returning
 * client with an approved KYC ended up being shown the "issue a card" offer all over again.
 */
export type OnboardingStage =
  | 'OFFER' | 'KYC' | 'FUNDED_AWAITING_KYC' | 'KYC_REJECTED' | 'REFUSED_REFUND'
  | 'PAYMENT' | 'PAYMENT_PENDING' | 'OPERATOR_ISSUE' | 'ISSUED' | 'INCONSISTENT';

export type OnboardingAction =
  | 'ACCEPT_OFFER' | 'UPLOAD_DOCUMENTS' | 'WAIT_KYC' | 'REVIEW_REQUESTED_DOCUMENTS'
  | 'PAY_CARD' | 'WAIT_PAYMENT' | 'SEND_MONEY' | 'PAY_REST' | 'WAIT_OPERATOR' | 'WAIT_ISSUE_NO_PAYMENT'
  | 'OPEN_CARD' | 'CONTACT_SUPPORT';

/** Non-null only when stage === 'INCONSISTENT'; 1:1 with the backend invariant guards. */
export type OnboardingInconsistency =
  | 'MULTIPLE_CURRENT_ATTEMPTS' | 'CARD_WITHOUT_APPROVAL'
  | 'PAYMENT_BEFORE_APPROVAL' | 'STUCK_AFTER_FUNDING';

export interface OnboardingSnapshot {
  stage: OnboardingStage;
  nextAction: OnboardingAction;
  inconsistencyCode: OnboardingInconsistency | null;
  offer: IssuanceOffer | null;
  kyc: { caseId: string; status: string; requestedDocs: string[] } | null;
  payment: {
    requestId: string;
    status: string;
    amount: string | null;
    currency: string | null;
    payInInstructions: string | null;
    fundingDeadline: string | null;
    /** Paid towards the card price so far — set only while part of it is still due (PAY_REST). */
    cardPricePaid?: string | null;
    /** Still to pay for the card — set only while it is due (PAY_REST). */
    cardPriceDue?: string | null;
  } | null;
  hasCard: boolean;
  /** Платит ли клиент за выпуск сам. False ⇒ карту оплачивают вне Tacita: цену ему не показываем. */
  paymentRequired: boolean;
  /**
   * How long the card usually takes from here, in minutes (DVL-003) — set only while the client waits for nothing but
   * the card; absent on an older server.
   */
  issueEtaMinutes?: number | null;
  updatedAt: string;
}

/**
 * Self-serve KYC / card-issuance (docs/CLIENT_KYC_AND_CARD_ISSUANCE.md §5). Mirrors backend
 * KycIssuanceController (/api/v1/showcase/me/card/issuance). WireDocType is a generic document class —
 * the issuing country is a separate field (§5), never baked into the type.
 */
export type WireDocType = 'PASSPORT' | 'ID' | 'PROOF_OF_ADDRESS' | 'SOURCE_OF_FUNDS' | 'SOURCE_OF_WEALTH' | 'OTHER';

/** All WireDocType values, in the order shown in the type picker. */
export const WIRE_DOC_TYPES: WireDocType[] = [
  'PASSPORT', 'ID', 'PROOF_OF_ADDRESS', 'SOURCE_OF_FUNDS', 'SOURCE_OF_WEALTH', 'OTHER',
];

/** Per-document metadata returned by the case view — never any ciphertext (§7). */
export interface DocView {
  id: string;
  type: string;
  country: string | null;
  sha256: string;
  status: string;
  filename: string;
  mime: string;
  sizeBytes: number;
  uploadedAt: string;
}

/**
 * Issuance case (§11). `status` drives the PWA screen: DOC_REQUIRED/READY_TO_EXPORT → still collecting;
 * IN_REVIEW/EXPORTED → under review; NEEDS_INFO → prompt for more docs; APPROVED/REJECTED → final.
 * `composeFlag` (RU_ONLY | NO_DOCUMENTS | null) is a SOFT hint — never rendered as an error (§5).
 */
export interface CaseView {
  caseId: string;
  status: string;
  issuanceState: string;
  composeFlag: string | null;
  offerVersion: string | null;
  exportVersion: number;
  createdAt: string;
  documents: DocView[];
  /** WireDocType names the reviewer asked the client to re-upload on a NEEDS_INFO round (types only, no raw text). */
  requestedDocs: string[];
  /** The issuance request's own support-chat ticket — the client messages the Tacita operator from this page. */
  ticketId: string | null;
}

/** One staged file for upload: the picked File, its assigned type, and an optional ISO issuing country. */
export interface IssuanceUploadItem {
  file: File;
  type: WireDocType;
  country?: string;
}

/**
 * Судьба одного присланного файла. Сервер отвечает по каждому: взят или нет и почему.
 * До этого ответ содержал только состояние дела, и отклонённый файл исчезал бесследно —
 * клиент видел успешную отправку, а документа не появлялось.
 */
export interface IssuanceFileOutcome {
  filename: string | null;
  accepted: boolean;
  /** Код причины отказа; сейчас единственный — DUPLICATE (такой файл уже загружен в это дело). */
  reason: string | null;
}

/** Ответ на загрузку: состояние дела плюс судьба каждого файла. */
export interface IssuanceUploadResult {
  caseView: CaseView;
  files: IssuanceFileOutcome[];
}

export type CardPinMode = 'NONE' | 'MANUAL' | 'API';
export type CardPinState = 'NONE' | 'PENDING' | 'SET';

export interface Capabilities {
  balance: boolean;
  transactions: boolean;
  sensitiveReveal: boolean;
  pin: CardPinMode;
  /** Card is bridged to a provider that relays OTP → the in-app OTP toggle is offered (docs §3.0). */
  inAppOtp: boolean;
  /** Card relays OTP AND Web Push VAPID is configured → the "Push" toggle is offered (rolled out to all clients). */
  pushOtp: boolean;
  /** Client is experimental → the new payments flow (crypto top-up) is shown in the top-up/pay method lists. */
  newPayments: boolean;
  /** Concierge mode is on for the client (global switch OR experimental, decided on the server) → may create
   *  concierge requests. UI only; the server checks it itself (CONCIERGE_PAYMENTS_MODE.md §5.1). */
  conciergePayments: boolean;
  /** The client has concierge requests → the payments tab keeps «Мои платежи» even with the mode off (§5.1,
   *  REQ-032). */
  conciergeHistory: boolean;
  /** The concierge crypto pipeline is on (§6.4) → the concierge pay form offers CRYPTO (USDT on TRON only). */
  conciergeCrypto: boolean;
  /** The card's provider really freezes cards → freeze/unfreeze buttons; false → ask the concierge (В-5). Absent on
   *  an older server. */
  cardFreeze?: boolean;
  /** The card's provider really blocks cards → a «Block» button (step-up gated); false → the concierge (В-5). */
  cardBlock?: boolean;
  /** The client has a card to name (В-5). */
  cardRename?: boolean;
  /** The master account «Общий баланс» is on for the client (docs/cabinet-redesign/04 §7): the home hero shows it and
   *  the top-up screen offers its permanent address. Absent on an older server. */
  masterAccount?: boolean;
}

/** Card PIN status (set-only — the value is never returned to the client). */
export interface CardPinStatus {
  mode: CardPinMode;
  state: CardPinState;
}

export interface Balance {
  amount: string | null;                // BigDecimal сериализован как string
  currency: string;
  asOf: string;
  sourceLabel: string;                  // "unavailable" → PWA скрывает плашку
}

export interface TransactionItem {
  id: string;                            // card_authorization.id
  createdAt: string;                     // ingestion time (local proxy для occurredAt)
  amount: string;                        // BigDecimal — единицы валюты
  currency: string;
  merchant: string | null;
  mcc: string | null;
  category: string | null;
  /**
   * A card operation's status (APPROVED / DECLINED / CLEARED / REVERSED), a top-up request's (category TOPUP), or the
   * state of a shared-balance movement (category MASTER_ARRIVAL / MASTER_TO_CARD — components/vitrine/txRow.ts).
   */
  status: string;
  description: string | null;
  /**
   * Our decline reason code of a DECLINED card operation (core ClientDeclineReason) — never the provider's text;
   * null when the reason is unknown or the row is not declined. Absent on an older core.
   */
  declineReason?: 'INSUFFICIENT_FUNDS' | 'LIMIT_EXCEEDED' | 'CARD_FROZEN' | 'CARD_BLOCKED' | 'CARD_EXPIRED'
    | 'INCORRECT_DETAILS' | 'SECURITY_CHECK' | 'MERCHANT_NOT_ALLOWED' | null;
  /** How our own top-up came in (category TOPUP); null for every other row. Absent on an older core. */
  topUpMethod?: 'CRYPTO' | 'BANK' | 'CASH' | null;
}

export interface TransactionsPage {
  items: TransactionItem[];
  pageNumber: number;
  hasNext: boolean;
}

/** File formats of the card activity report. */
export type StatementFileFormat = 'PDF' | 'CSV';

/** A History chip — filtered on the server (docs/STATEMENTS_SPEC.md §5.2). */
export type ActivityDirection = 'INCOME' | 'EXPENSE' | 'TRANSFER';

/** The History filter: a period of local days, one chip, the device zone. */
export interface ActivityQuery {
  from?: string;
  to?: string;
  direction?: ActivityDirection;
  tz?: string;
}

/** Totals of a period in one shown currency (REQ-013): income/expense of completed rows, the rest signed. */
export interface CurrencyTotals {
  currency: string;
  income: string;
  expense: string;
  inProgress: string;
  reversed: string;
}

export interface SensitivePayload {
  panCiphertext: string;
  cvvCiphertext: string;
  algorithm: string;
  meta: Record<string, unknown>;
}

export interface EncryptionEnvelopeRequest {
  algorithm: string;
  publicKeyBase64: string;
  metadata?: Record<string, string>;
}

export const vitrineMeApi = {
  /** Returns 204 No Content; ошибка → axios rejects (caller игнорирует best-effort). */
  async logout(): Promise<void> {
    await http.post<void>('/v1/showcase/me/logout');
  },

  changePassword(oldPassword: string, newPassword: string) {
    return http
      .post<{ status: string }>('/v1/showcase/me/password/change', { oldPassword, newPassword })
      .then(r => r.data);
  },
};

export const vitrineCardApi = {
  me() {
    return http.get<MyProfile>('/v1/showcase/me').then(r => r.data);
  },

  /**
   * Persist the client's language choice server-side. Emails and pushes are rendered by the backend, so a
   * language that lives only in localStorage means every notification stays English no matter what the client
   * picked in the UI. Best-effort by design — see the caller.
   */
  setLanguage(language: string) {
    return http.put<{ language: string }>('/v1/showcase/me/language', { language }).then(r => r.data);
  },

  card() {
    return http.get<MyCard>('/v1/showcase/me/card').then(r => r.data);
  },

  /**
   * Primary card-side fetch for the LK. Unlike GET /me/card (which answers 409 CARD_NOT_ISSUED for a
   * card-less client), this
   * returns a graceful CardOverview: either the card, or the issuance offer to show the no-card hero (§3/§4).
   */
  cardOverview() {
    return http.get<CardOverview>('/v1/showcase/me/card/overview').then(r => r.data);
  },

  /**
   * Where the client is in onboarding and what to do next, decided server-side. Card-less tolerant: always 200,
   * so this is safe to call on every entry (login, refresh, deep-link) as the routing source of truth.
   */
  onboarding() {
    return http.get<OnboardingSnapshot>('/v1/showcase/me/card/onboarding').then(r => r.data);
  },

  capabilities() {
    return http.get<Capabilities>('/v1/showcase/me/card/capabilities').then(r => r.data);
  },

  pinStatus() {
    return http.get<CardPinStatus>('/v1/showcase/me/card/pin').then(r => r.data);
  },

  /** Set/change the card PIN. Pass a single-use step-up grant (from passkey / email-OTP). */
  changePin(newPin: string, opts?: { grant?: string }) {
    const config = opts?.grant ? { headers: { 'X-Reveal-Grant': opts.grant } } : undefined;
    return http.post<{ state: CardPinState }>('/v1/showcase/me/card/pin/change', { newPin }, config)
      .then(r => r.data);
  },

  /**
   * Freeze / unfreeze the card where its provider supports it (`cardFreeze`). 405 FEATURE_NOT_SUPPORTED otherwise,
   * 409 CARD_STATUS_CONFLICT for a card in the wrong state. Returns the updated card. Unfreeze re-enables spending:
   * step-up gated like block (403 STEP_UP_REQUIRED → call again with the grant, purpose CARD_UNFREEZE), and
   * 409 CARD_FROZEN_BY_OPERATOR when the client did not set this freeze — only the concierge can lift it.
   */
  freeze() {
    return http.post<MyCard>('/v1/showcase/me/card/freeze').then(r => r.data);
  },

  unfreeze(opts?: { grant?: string }) {
    const config = opts?.grant ? { headers: { 'X-Reveal-Grant': opts.grant } } : undefined;
    return http.post<MyCard>('/v1/showcase/me/card/unfreeze', undefined, config).then(r => r.data);
  },

  /**
   * Block the card (irreversible). Step-up gated: without a grant the server answers 403 STEP_UP_REQUIRED with the
   * factors; call again with the grant from the chooser (purpose CARD_BLOCK).
   */
  block(opts?: { grant?: string }) {
    const config = opts?.grant ? { headers: { 'X-Reveal-Grant': opts.grant } } : undefined;
    return http.post<MyCard>('/v1/showcase/me/card/block', undefined, config).then(r => r.data);
  },

  /** Set the card's name; blank/null clears it. 422 CARD_NAME_TOO_LONG / CARD_NAME_INVALID. */
  rename(name: string | null) {
    return http.patch<MyCard>('/v1/showcase/me/card', { name }).then(r => r.data);
  },

  /** Cancel the customer's own pending MANUAL PIN request. */
  cancelPin() {
    return http.delete<void>('/v1/showcase/me/card/pin/change').then(() => undefined);
  },

  balance() {
    return http.get<Balance>('/v1/showcase/me/card/balance').then(r => r.data);
  },

  /**
   * Per Round 19 C2 — backend читает из локальной card_authorization (не live SDK).
   * Page/size pagination — Spring Data PageRequest.
   */
  transactions(params?: { page?: number; size?: number } & ActivityQuery) {
    return http
      .get<TransactionsPage>('/v1/showcase/me/card/transactions', {
        params: {
          page: params?.page ?? undefined,
          size: params?.size ?? undefined,
          from: params?.from || undefined,
          to: params?.to || undefined,
          direction: params?.direction ?? undefined,
          tz: params?.tz ?? undefined,
        },
      })
      .then(r => r.data);
  },

  /**
   * Download the card activity report of a period and chip (docs/STATEMENTS_SPEC.md §5.7) — always the signed-in
   * client's own; the server names the file.
   */
  downloadStatement(q: { from: string; to: string; direction?: ActivityDirection; tz?: string },
                    format: StatementFileFormat) {
    const params: Record<string, string> = { from: q.from, to: q.to, format };
    if (q.direction) params.direction = q.direction;
    if (q.tz) params.tz = q.tz;
    return downloadBlob('/v1/showcase/me/statements', params,
      `tacita_card_activity_${q.from}_${q.to}.${format.toLowerCase()}`);
  },

  /** Totals of the whole period for the History summary — every row of the period, whatever chip is on. */
  transactionTotals(params: { from: string; to: string; tz?: string }) {
    return http
      .get<{ totals: CurrencyTotals[] }>('/v1/showcase/me/card/transactions/totals', { params })
      .then(r => r.data.totals);
  },

  /** Per Round 19 H8 — force-sync trigger. Returns 202 если queued, 204 если external API не настроен. */
  refreshTransactions() {
    return http.post<{ status: string }>('/v1/showcase/me/card/transactions/refresh').then(r => r.data);
  },

  sensitive(req: EncryptionEnvelopeRequest, revealGrant?: string) {
    const config = revealGrant ? { headers: { 'X-Reveal-Grant': revealGrant } } : undefined;
    return http
      .post<SensitivePayload>('/v1/showcase/me/card/sensitive-data', req, config)
      .then(r => r.data);
  },

  // ── Self-serve KYC / card issuance (docs §4–§5) ──

  /**
   * Create or return the active issuance case (§4). Body carries the consented offer version (audit intent).
   * 409 if the client already has a card — the caller handles that gracefully (not an error to the client).
   */
  openIssuanceCase(offerVersion: string | null) {
    return http.post<CaseView>('/v1/showcase/me/card/issuance', { offerVersion }).then(r => r.data);
  },

  offerPdf(locale: string) {
    return http.get<Blob>('/v1/showcase/me/card/issuance/offer/pdf', {
      params: { locale }, responseType: 'blob',
    }).then(r => r.data);
  },

  /** Latest case + per-document statuses for the upload/progress screen (§5). 404 when no case exists. */
  getIssuanceCase() {
    return http.get<CaseView>('/v1/showcase/me/card/issuance').then(r => r.data);
  },

  /**
   * Multipart document upload (§5). `files` / `types` are index-aligned; `countries` is sent per file ONLY when
   * at least one file carries a country (backend requires the countries list to match files.size exactly) — an
   * empty entry is trimmed to null server-side. Axios sets the multipart boundary itself, so no Content-Type here.
   */
  uploadIssuanceDocuments(items: IssuanceUploadItem[], onProgress?: (percent: number) => void) {
    const form = new FormData();
    const anyCountry = items.some(it => !!it.country && it.country.trim() !== '');
    for (const it of items) {
      form.append('files', it.file);
      form.append('types', it.type);
      if (anyCountry) form.append('countries', it.country ?? '');
    }
    return http
      .post<IssuanceUploadResult>('/v1/showcase/me/card/issuance/documents', form, {
        // The shared instance times out at 30 s, which is right for a JSON call and wrong for an upload:
        // axios counts the whole request, body included, so a few megabytes over mobile data simply do not
        // fit. On 25.09 a client's passport aborted seven times in a row at ~31% before the eighth got
        // through — nginx logged 499 (client went away) each time, the backend never saw a request, and the
        // screen could only say "error". Uploads get their own budget.
        timeout: UPLOAD_TIMEOUT_MS,
        onUploadProgress: e => {
          if (onProgress && e.total) onProgress(Math.round((e.loaded / e.total) * 100));
        },
      })
      .then(r => r.data);
  },
};
